import { BaseElement } from 'base';
import { bus } from 'bus';

const RAISED = 'toast:raised';
const DISMISSED = 'toast:dismissed';
const LIFETIME_MS = 4000;
const MAX_TOASTS = 3;

const isValidId = id =>
  (typeof id === 'string' && id !== '') || typeof id === 'number';

class ToastStack extends BaseElement {
  #list = null;
  #proto = null;
  #toasts = [];
  #seen = new Set();
  #holds = new Set();
  #origin = null;

  onConnect() {
    this.#list = this.q(':scope > .toast-stack__list');
    this.#proto = this.q(':scope > template')?.content.lastElementChild ?? null;
    this.#toasts = [];
    this.#seen = new Set();
    this.#holds = new Set();
    this.#origin = null;

    this.qa(':scope > .toast-stack__list > inline-message').forEach(node =>
      node.remove()
    );

    if (!this.#list || this.#proto?.localName !== 'inline-message') {
      console.error('[toast-stack] missing list or message template');
      return;
    }

    this.cleanup(() =>
      this.#toasts.forEach(toast => clearTimeout(toast.timer))
    );
    this.listen(this, 'pointerover', () => this.#hold('pointer', true));
    this.listen(this, 'pointerout', event => {
      if (!this.contains(event.relatedTarget)) this.#hold('pointer', false);
    });
    this.listen(this, 'focusin', this.#onFocusIn);
    this.listen(this, 'focusout', this.#onFocusOut);
    this.subscribeLive(RAISED, this.#onRaised);
    this.subscribeLive(DISMISSED, this.#onDismissed);
  }

  async #onRaised(value) {
    const id = value?.id;
    const text = typeof value?.text === 'string' ? value.text.trim() : '';
    const validStyle = value?.style === undefined || value.style === 'warning';
    if (!isValidId(id) || !text || !validStyle) {
      console.error(
        `[toast-stack] "${RAISED}" expects { id, text }, received:`,
        value
      );
      return;
    }
    if (this.#seen.has(id)) return;
    this.#seen.add(id);

    await customElements.whenDefined('inline-message');
    if (!this.isConnected) return;
    this.#add(id, text);
  }

  #onDismissed(value) {
    if (!isValidId(value?.id)) {
      console.error(
        `[toast-stack] "${DISMISSED}" expects { id }, received:`,
        value
      );
      return;
    }
    const toast = this.#toasts.find(item => item.id === value.id);
    if (toast) this.#remove(toast);
  }

  #add(id, text) {
    while (this.#toasts.length >= MAX_TOASTS) this.#remove(this.#toasts[0]);

    const node = document.importNode(this.#proto, true);
    node.removeAttribute('aria-live');
    const toast = {
      id,
      node,
      remaining: LIFETIME_MS,
      startedAt: 0,
      timer: null
    };
    this.#toasts.push(toast);
    this.#list.prepend(node);
    node.text = text;
    node.run(() => {
      this.#remove(toast, true);
      bus.publish(DISMISSED, { id });
    });
    if (this.#holds.size === 0) this.#start(toast);
  }

  #start(toast) {
    toast.startedAt = performance.now();
    toast.timer = setTimeout(() => this.#remove(toast), toast.remaining);
  }

  #hold(reason, isHeld) {
    const was = this.#holds.size > 0;
    if (isHeld) {
      this.#holds.add(reason);
    } else {
      this.#holds.delete(reason);
    }
    const held = this.#holds.size > 0;
    if (held === was) return;

    const now = performance.now();
    this.#toasts.forEach(toast => {
      if (!held) {
        this.#start(toast);
        return;
      }
      clearTimeout(toast.timer);
      toast.timer = null;
      toast.remaining = Math.max(0, toast.remaining - (now - toast.startedAt));
    });
  }

  #remove(toast, dismissed = false) {
    const index = this.#toasts.indexOf(toast);
    if (index === -1) return;
    clearTimeout(toast.timer);
    this.#toasts.splice(index, 1);

    const origin = this.#origin;
    const active = document.activeElement;
    const hadFocus =
      toast.node.contains(active) ||
      (dismissed && Boolean(origin) && active === document.body);
    const hovered = toast.node.matches(':hover');
    toast.node.remove();
    if (hovered) this.#hold('pointer', false);
    if (hadFocus) this.#restoreFocus(origin);
  }

  #restoreFocus(origin) {
    this.#origin = null;
    this.#hold('focus', false);
    if (origin?.isConnected && !this.contains(origin)) {
      origin.focus({ preventScroll: true });
      if (document.activeElement === origin) return;
    }
    this.tabIndex = -1;
    this.focus({ preventScroll: true });
  }

  #onFocusIn(event) {
    if (event.target === this) return;
    if (!this.contains(event.relatedTarget)) this.#origin = event.relatedTarget;
    this.#hold('focus', true);
  }

  #onFocusOut(event) {
    if (event.target === this) this.removeAttribute('tabindex');
    const next = event.relatedTarget;
    if (next && next !== this && this.contains(next)) return;
    this.#origin = null;
    this.#hold('focus', false);
  }
}

if (!customElements.get('toast-stack'))
  customElements.define('toast-stack', ToastStack);
